import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AttendanceSetting, CreateHolidayResponse, HolidaysResponse } from "@mubin/contracts";
import { api, errorText, send } from "../lib/api";
import { Empty, ErrorNote, Field, Loading, useSubmit } from "./ui";

const HOURS = [0, 1, 2, 3, 4, 5, 6];
const hourLabel = (h: number) => (h === 0 ? "منتصف الليل" : `${h}:00 فجرًا`);

/**
 * When a study day closes (B14, 2026-10-18): attending is reciting, so a student with portions due who has
 * not recited by this hour the next morning is recorded absent, and a recitation before it counts for the
 * previous day.
 */
export function AttendanceDayCard({
  organizationId,
  value,
}: {
  organizationId: string;
  value: AttendanceSetting;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [hour, setHour] = useState(value.dayCutoffHour);
  const [saved, setSaved] = useState(false);
  const changed = hour !== value.dayCutoffHour;

  function save(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    void run(async () => {
      await send("PATCH", `/v1/organizations/${organizationId}/settings`, {
        attendance: { dayCutoffHour: hour },
      });
      await qc.invalidateQueries({ queryKey: ["organizations"] });
      setSaved(true);
    }, errorText);
  }

  return (
    <section className="card">
      <div className="card-h">
        <h2>الحضور التلقائي</h2>
      </div>
      <form className="form section" onSubmit={save}>
        <p className="muted small">
          الحضور هو التسميع: من سمّع في يوم دراسي سُجّل حاضرًا، ومن لم يسمّع حتى إقفال اليوم سُجّل غائبًا
          وطُبّقت على خطته سياسة الغياب. «غائب بعذر» يسجله المعلم أو المشرف يدويًا.
        </p>
        <Field
          id="att-cutoff"
          label="إقفال اليوم الدراسي (صباح اليوم التالي، بتوقيت الجمعية)"
          hint="التسميع قبل هذه الساعة يُحسب لليوم السابق"
        >
          <select id="att-cutoff" value={hour} onChange={(e) => setHour(Number(e.target.value))}>
            {HOURS.map((h) => (
              <option key={h} value={h}>
                {hourLabel(h)}
              </option>
            ))}
          </select>
        </Field>
        <ErrorNote error={error} />
        {saved && !changed && <p className="muted small">حُفظ، ويسري من اليوم التالي.</p>}
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !changed}>
            {busy ? "جارٍ الحفظ…" : "حفظ"}
          </button>
        </div>
      </form>
    </section>
  );
}

/** Organization holidays: no attendance is expected and plans do not advance on these days. */
export function HolidaysCard({ organizationId }: { organizationId: string }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["holidays", organizationId],
    queryFn: () => api<HolidaysResponse>(`/v1/holidays?organizationId=${organizationId}`),
  });
  const { busy, error, run } = useSubmit();
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState<string | null>(null);

  function add(e: FormEvent) {
    e.preventDefault();
    setNote(null);
    void run(async () => {
      const res = await send<CreateHolidayResponse>("POST", "/v1/holidays", { organizationId, date, name });
      setNote(res.plansMoved ? `أُضيفت، ونُقلت مقررات ${res.plansMoved} خطة إلى ما بعدها.` : "أُضيفت.");
      setDate("");
      setName("");
      await qc.invalidateQueries({ queryKey: ["holidays", organizationId] });
    }, errorText);
  }

  const remove = (id: string) =>
    void run(async () => {
      await api(`/v1/holidays/${id}`, { method: "DELETE" });
      await qc.invalidateQueries({ queryKey: ["holidays", organizationId] });
    }, errorText);

  return (
    <section className="card">
      <div className="card-h">
        <h2>الإجازات</h2>
      </div>
      <div className="section">
        <p className="muted small">
          في يوم الإجازة لا يُسجَّل غياب ولا تتقدم الخطط. إضافة إجازة على أيام وُلّدت لها مقررات تنقل المقررات
          إلى ما بعدها، ما لم يكن الطالب قد سمّع منها. حذف الإجازة يسري على ما يُولَّد بعده فقط.
        </p>
        {list.isPending ? (
          <Loading rows={2} />
        ) : !list.data?.holidays.length ? (
          <Empty title="لا إجازات بعد" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>اليوم</th>
                  <th>الاسم</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.holidays.map((h) => (
                  <tr key={h.id}>
                    <td>
                      <bdi dir="ltr" className="num">
                        {h.date}
                      </bdi>
                    </td>
                    <td>{h.name}</td>
                    <td className="actions">
                      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => remove(h.id)}>
                        حذف
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <form className="form section" onSubmit={add}>
        <div className="form-row">
          <Field id="hol-date" label="اليوم">
            <input
              id="hol-date"
              type="date"
              dir="ltr"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field id="hol-name" label="الاسم">
            <input
              id="hol-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="إجازة اليوم الوطني"
            />
          </Field>
        </div>
        <ErrorNote error={error} />
        {note && <p className="muted small">{note}</p>}
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !date || name.trim().length < 2}>
            {busy ? "جارٍ الحفظ…" : "إضافة إجازة"}
          </button>
        </div>
      </form>
    </section>
  );
}
